/**
 * Trade juice — screen feedback on a sell fill.
 * Profit: green edge-flash + a rising profit number. Loss: red flash + shake.
 * Pure DOM, no game state. Intensity scales in 3 tiers with the |P/L %|.
 */
import { formatNumber } from '../game/utils.js';

function ensureLayer(): HTMLElement {
  let layer = document.getElementById('juice-layer');
  if (!layer) {
    layer = document.createElement('div');
    layer.id = 'juice-layer';
    document.body.appendChild(layer);
  }
  return layer;
}

/** 1 = normal, 2 = big (>=50%), 3 = huge (>=100%). */
function tier(absPct: number): 1 | 2 | 3 {
  if (absPct >= 100) return 3;
  if (absPct >= 50) return 2;
  return 1;
}

/**
 * Fire the sell-fill feedback (only on an actual buy/sell tap, never ambient).
 * The floating number always shows; the full-screen colour flash + shake only
 * fire for a notable trade (tier 2+, i.e. |P/L| >= 50%) so routine trades stay
 * calm and don't feel spammy.
 */
export function tradeJuice(pnlPct: number, pnlAmount: number): void {
  const layer = ensureLayer();
  const win = pnlAmount >= 0;
  const t = tier(Math.abs(pnlPct));

  // Number pop always (brief, satisfying, self-removes).
  const pop = document.createElement('div');
  pop.className = `juice-pop ${win ? 'ex-profit' : 'ex-loss'} juice-t${t}`;
  const sign = pnlAmount >= 0 ? '+' : '';
  pop.textContent = `${sign}${formatNumber(pnlAmount)} (${sign}${pnlPct.toFixed(1)}%)`;
  pop.addEventListener('animationend', () => pop.remove());
  layer.appendChild(pop);

  // Full-screen flash + shake reserved for notable trades only.
  if (t < 2) return;

  const flash = document.createElement('div');
  flash.className = `juice-flash ${win ? 'juice-win' : 'juice-loss'} juice-t${t}`;
  flash.addEventListener('animationend', () => flash.remove());
  layer.appendChild(flash);

  if (!win) {
    const root = document.getElementById('screen-content') || document.body;
    root.classList.add('juice-shake', `juice-shake-t${t}`);
    setTimeout(() => root.classList.remove('juice-shake', `juice-shake-t${t}`), 480);
  }
}
